import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import publicApi from '../services/publicApi';

const formatLargeNumber = (num) => {
  if (!num || isNaN(num)) return 'N/A';
  
  if (num >= 1e12) {
    return (num / 1e12).toFixed(2) + 'T';
  } else if (num >= 1e9) {
    return (num / 1e9).toFixed(2) + 'B';
  } else if (num >= 1e6) {
    return (num / 1e6).toFixed(2) + 'M';
  } else if (num >= 1e3) {
    return (num / 1e3).toFixed(2) + 'K';
  } else {
    return num.toLocaleString();
  }
};

const formatCurrency = (value) => {
  if (!value || isNaN(value)) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 6
  }).format(value);
};

const MarketData = ({ coinSymbol }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMarketData = async () => {
      if (!coinSymbol) return;
      
      setLoading(true);
      setError(null);
      
      try {
        console.log(`[MarketData] Fetching market data for ${coinSymbol}`);
        const response = await publicApi.get(`/api/public/coins/${coinSymbol}/data`);
        setData(response.data);
        console.log(`[MarketData] Got market data:`, response.data);
      } catch (err) {
        console.error(`[MarketData] Error fetching market data:`, err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMarketData();
  }, [coinSymbol]);

  if (loading) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 40 }}>
            <CircularProgress size={16} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return null; // Don't show anything if there's an error or no data
  }

  const { numerical } = data;
  const hasValidNumerical = numerical && Object.keys(numerical).length > 0;

  if (!hasValidNumerical) {
    return null; // Don't show market data if no numerical data available
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600 }}>
          {t('coin.analysis.marketData', { defaultValue: 'Market Data' })}
        </Typography>
        
        <Grid container spacing={1.5}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
              {t('coin.analysis.marketCap', { defaultValue: 'Market Cap' })}
            </Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.875rem' }}>
              ${formatLargeNumber(numerical.market_cap)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
              {t('coin.analysis.volume24h', { defaultValue: '24h Volume' })}
            </Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.875rem' }}>
              ${formatLargeNumber(numerical.volume_24h)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
              {t('coin.analysis.marketRank', { defaultValue: 'Market Rank' })}
            </Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.875rem' }}>
              #{numerical.market_cap_rank || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
              {t('coin.analysis.circulatingSupply', { defaultValue: 'Circulating Supply' })}
            </Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.875rem' }}>
              {formatLargeNumber(numerical.circulating_supply)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
              {t('coin.analysis.totalSupply', { defaultValue: 'Total Supply' })}
            </Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.875rem' }}>
              {formatLargeNumber(numerical.total_supply)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
              {t('coin.analysis.allTimeHigh', { defaultValue: 'All Time High' })}
            </Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.875rem' }}>
              {formatCurrency(numerical.ath)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
              {t('coin.analysis.allTimeLow', { defaultValue: 'All Time Low' })}
            </Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.875rem' }}>
              {formatCurrency(numerical.atl)}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default MarketData;